import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  BookOpen, 
  Edit, 
  Camera,
  Save,
  X,
  Loader
} from "lucide-react";
import { constants } from "../../constants";
import { ToastContainer, toast } from "react-toastify";

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { serverURL } = constants;
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: "",
    bio: "",
    mobile_no: "",
    country_code: "",
    city: "",
    state: "",
    country: "",
    branch: "",
    intake_year: "",
    intake: "",
    year_of_study: "",
    purpose: "",
    counselling_mode: "",
  });

  useEffect(() => {
    fetchUserProfile();
  }, [id]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${serverURL}/api/profile/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUser(response.data.user);
      setFormData({
        displayName: response.data.user.displayName || "",
        bio: response.data.user.bio || "",
        mobile_no: response.data.user.mobile_no || "",
        country_code: response.data.user.country_code || "",
        city: response.data.user.city || "",
        state: response.data.user.state || "",
        country: response.data.user.country || "",
        branch: response.data.user.branch || "",
        intake_year: response.data.user.intake_year || "",
        intake: response.data.user.intake || "",
        year_of_study: response.data.user.year_of_study || "",
        purpose: response.data.user.purpose || "",
        counselling_mode: response.data.user.counselling_mode || "",
      });
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch profile");
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataImage = new FormData();
    formDataImage.append("profileImage", file);

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${serverURL}/api/profile/${id}/upload-picture`,
        formDataImage,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      setUser(prev => ({ ...prev, photoURL: response.data.photoURL }));
      toast.success("Profile picture updated successfully");
      setUploading(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to upload image");
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${serverURL}/api/profile/${id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setUser(response.data.user);
      setIsEditing(false);
      toast.success("Profile updated successfully");
      setSaving(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchUserProfile();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-cream flex items-center justify-center">
        <Loader className="w-12 h-12 text-primary-brown animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-primary-cream flex items-center justify-center">
        <p className="text-xl text-primary-brown">User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-cream py-8 px-4 sm:px-6 lg:px-8">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        theme="light"
      />
      
      <div className="max-w-5xl mx-auto">
        {/* Header Card */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-white to-primary-blue h-24"></div>
          
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end -mt-16 sm:-mt-20">
              {/* Profile Picture */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-primary-cream">
                  {user.photoURL ? (
                    <img
                      src={`${serverURL}${user.photoURL}`}
                      alt={user.displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-16 h-16 text-primary-brown" />
                    </div>
                  )}
                </div>
                
                <label className="absolute bottom-0 right-0 bg-primary-brown text-white p-2 rounded-full cursor-pointer hover:bg-primary transition-all shadow-lg">
                  <Camera className="w-5 h-5" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                
                {uploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <Loader className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>
              
              {/* User Info */}
              <div className="mt-4 sm:mt-0 sm:ml-6 flex-1 text-center sm:text-left">
                <h1 className="text-3xl font-bold text-black">
                  {user.displayName}
                </h1>
                <p className="text-primary-blue flex items-center justify-center sm:justify-start gap-2 mt-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </p>
              </div>
              
              {/* Edit Button */}
              <div className="mt-4 sm:mt-0">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-6 py-2 bg-primary-brown text-white rounded-lg hover:bg-primary transition-all"
                  >
                    <Edit className="w-5 h-5" />
                    Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                    >
                      {saving ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <Save className="w-5 h-5" />
                      )}
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all disabled:opacity-50"
                    >
                      <X className="w-5 h-5" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Bio & Contact */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio Section */}
            <div className="bg-white shadow-lg rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-primary-brown mb-4">About</h2>
              {isEditing ? (
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none min-h-32"
                  placeholder="Tell us about yourself..."
                />
              ) : (
                <p className="text-gray-700">
                  {user.bio || "No bio added yet."}
                </p>
              )}
            </div>

            {/* Personal Information */}
            <div className="bg-white shadow-lg rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-primary-brown mb-4">Personal Information</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.displayName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleChange}
                        placeholder="+91"
                        className="w-20 px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                      />
                      <input
                        type="text"
                        name="mobile_no"
                        value={formData.mobile_no}
                        onChange={handleChange}
                        className="flex-1 px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                      />
                    </div>
                  ) : (
                    <p className="text-gray-700">
                      {user.country_code && user.mobile_no 
                        ? `${user.country_code} ${user.mobile_no}` 
                        : "Not provided"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    City
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.city || "Not provided"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    State
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.state || "Not provided"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Country
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.country || "Not provided"}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="bg-white shadow-lg rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-primary-brown mb-4">Academic Information</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Branch/Field
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="branch"
                      value={formData.branch}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.branch || "Not provided"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Year of Study
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="year_of_study"
                      value={formData.year_of_study}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.year_of_study || "Not provided"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Intake Year
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="intake_year"
                      value={formData.intake_year}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.intake_year || "Not provided"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Intake
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="intake"
                      value={formData.intake}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.intake || "Not provided"}</p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Purpose
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.purpose || "Not provided"}</p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-primary-brown mb-2">
                    Counselling Mode
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="counselling_mode"
                      value={formData.counselling_mode}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary-sky rounded-lg focus:ring-2 focus:ring-primary-accent focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-700">{user.counselling_mode || "Not provided"}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Quick Stats & Courses */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white shadow-lg rounded-2xl p-6">
              <h2 className="text-xl font-bold text-primary-brown mb-4">Quick Stats</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-primary-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Member Since</p>
                    <p className="font-semibold text-primary-brown">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-primary-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-semibold text-primary-brown">
                      {user.verified ? "Verified" : "Not Verified"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enrolled Courses */}
            <div className="bg-white shadow-lg rounded-2xl p-6">
              <h2 className="text-xl font-bold text-primary-brown mb-4 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                Enrolled Courses
              </h2>
              
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary-cream rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-primary-brown" />
                </div>
                <p className="text-gray-600">Not enrolled in any course</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;